import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Copy, ExternalLink, Edit2, Trash2, UploadCloud } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader, TablePagination } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast, errorToast } from "../../shared/Toast";
import { listMedia, uploadMedia, renameMedia, deleteMedia, MEDIA_LIMITS } from "../../utils/mediaApi";
import { inputStyle, buttonStyle, btnSecondary, btnDanger, iconBtn, notice } from "../pages/pageStyles";

/**
 * Media — the merchant's image library. Upload an image once and copy its permanent public link
 * into a page, an email, an app banner or a social post.
 *
 * Merchant-scoped like Pages. Operations users browse and copy links only: the axios interceptor
 * signs a user out on ANY 403, so every write is hidden here rather than left for the server to refuse.
 */

const PAGE_LIMIT = 24;
const PAGE_SIZE_OPTIONS = [24, 48, 96];

const ACCEPT = MEDIA_LIMITS.imageMimeTypes.join(",");
const TYPES_LABEL = "JPG, PNG or WebP";

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    successToast("Link copied");
  } catch (e) {
    errorToast("Could not copy — open the image and copy its address by hand");
  }
};

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const nameOf = (item) => item.title || item.originalName || `Image ${item.id}`;

/** The browser-side half of the server's checks, so a wrong file is named before anything uploads. */
const checkFiles = (files) => {
  if (files.length > MEDIA_LIMITS.perUpload) return `Upload up to ${MEDIA_LIMITS.perUpload} images at a time`;
  for (const f of files) {
    if (!MEDIA_LIMITS.imageMimeTypes.includes(f.type)) return `${f.name}: image must be ${TYPES_LABEL}`;
    if (f.size > MEDIA_LIMITS.imageBytes) return `${f.name}: image must be 5 MB or smaller`;
  }
  return null;
};

const card = {
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  overflow: "hidden",
  background: C.white,
  display: "flex",
  flexDirection: "column",
};

const thumb = {
  aspectRatio: "1 / 1",
  background: `repeating-conic-gradient(${C.bg} 0% 25%, ${C.white} 0% 50%) 50% / 16px 16px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
};

const MediaLibrary = () => {
  const userType = localStorage.getItem("userType");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const merchantId = isMerchantScoped(userType) ? localStorage.getItem("MerchantId") : reduxSelectedMerchant;
  const readOnly = isReadOnly(userType);
  const noMerchant = !merchantId || merchantId === "all";

  const fileInput = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [editing, setEditing] = useState(null); // { id, title }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (noMerchant) { setData({ items: [], total: 0 }); return; }
    setLoading(true);
    const d = await listMedia({ merchantId, search: search || undefined, page, limit });
    setData(d);
    setLoading(false);
  }, [merchantId, noMerchant, search, page, limit]);

  useEffect(() => { load(); }, [load]);

  const applySearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || readOnly) return;
    const problem = checkFiles(files);
    if (problem) { errorToast(problem); return; }
    setUploading(true);
    try {
      const res = await uploadMedia(merchantId, files);
      if (res) {
        successToast(files.length === 1 ? "Image uploaded" : `${files.length} images uploaded`);
        if (page !== 1) setPage(1); else await load();
      }
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    upload(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer?.files);
  };

  const saveTitle = async () => {
    if (!editing) return;
    const { id, title } = editing;
    if (title.trim().length > MEDIA_LIMITS.title) { errorToast(`Name must be ${MEDIA_LIMITS.title} characters or fewer`); return; }
    const updated = await renameMedia(id, title.trim());
    if (updated) {
      setData((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, title: updated.title } : i)) }));
      setEditing(null);
    }
  };

  const runDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      if (await deleteMedia(confirmDelete.id)) {
        successToast("Image deleted");
        setConfirmDelete(null);
        if (data.items.length === 1 && page > 1) setPage(page - 1); else await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const dropProps = readOnly ? {} : {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop,
  };

  return (
    <Fragment>
      <Breadcrumb title="Media" parent="Merchant" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {noMerchant ? (
            <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Select a merchant to manage its images.</div></RCard>
          ) : (
            <RCard>
              <div {...dropProps} data-testid="media-drop" style={{ outline: dragOver ? `2px dashed ${C.coral}` : "none", outlineOffset: 6, borderRadius: 12 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
                  <form onSubmit={applySearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 240 }}>
                    <input style={{ ...inputStyle, flex: 1 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name…" />
                    <button type="submit" style={btnSecondary}>Search</button>
                  </form>
                  {!readOnly && (
                    <>
                      <input ref={fileInput} type="file" multiple accept={ACCEPT} onChange={onPick} style={{ display: "none" }} data-testid="media-file-input" />
                      <button type="button" style={{ ...buttonStyle, display: "inline-flex", alignItems: "center", gap: 6 }} disabled={uploading} onClick={() => fileInput.current?.click()}>
                        <UploadCloud size={15} /> {uploading ? "Uploading…" : "Upload images"}
                      </button>
                    </>
                  )}
                </div>

                {!readOnly && (
                  <div style={{ fontSize: 12, color: C.textLight, marginTop: -8, marginBottom: 16 }}>
                    {TYPES_LABEL}, up to 5 MB each, {MEDIA_LIMITS.perUpload} at a time. Drop files here or use the button. Images are compressed, resized to fit 2560px and stripped of photo data such as location. Each image gets a permanent public link.
                  </div>
                )}

                {loading && data.items.length === 0 ? <Loader /> : data.items.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                    {search ? "No images match this search." : readOnly ? "No images yet." : "No images yet. Upload one to get a link you can use anywhere."}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
                      {data.items.map((item) => {
                        const isEditing = editing?.id === item.id;
                        return (
                          <div key={item.id} style={card} data-testid={`media-card-${item.id}`}>
                            <a href={item.url} target="_blank" rel="noopener noreferrer" style={thumb} title="Open full size">
                              <img src={item.url} alt={nameOf(item)} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            </a>
                            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                              {isEditing ? (
                                <form onSubmit={(e) => { e.preventDefault(); saveTitle(); }} style={{ display: "flex", gap: 4 }}>
                                  <input
                                    autoFocus
                                    aria-label="Image name"
                                    style={{ ...inputStyle, padding: "5px 8px", fontSize: 12 }}
                                    value={editing.title}
                                    maxLength={MEDIA_LIMITS.title}
                                    placeholder={item.originalName}
                                    onChange={(e) => setEditing({ id: item.id, title: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
                                  />
                                  <button type="submit" style={{ ...btnSecondary, padding: "5px 8px", fontSize: 12 }}>Save</button>
                                </form>
                              ) : (
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={nameOf(item)}>
                                  {nameOf(item)}
                                </div>
                              )}
                              <div style={{ fontSize: 11, color: C.textLight }}>
                                {item.width && item.height ? `${item.width}×${item.height} · ` : ""}{fmtSize(item.sizeBytes)}
                              </div>
                              <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
                                <button type="button" style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5 }} onClick={() => copy(item.url)} aria-label={`Copy link to ${nameOf(item)}`}>
                                  <Copy size={12} /> Copy link
                                </button>
                                <a href={item.url} target="_blank" rel="noopener noreferrer" style={iconBtn} title="Open" aria-label={`Open ${nameOf(item)}`}><ExternalLink size={12} /></a>
                                {!readOnly && (
                                  <>
                                    <button type="button" style={iconBtn} title="Rename" aria-label={`Rename ${nameOf(item)}`} onClick={() => setEditing({ id: item.id, title: item.title || "" })}><Edit2 size={12} /></button>
                                    <button type="button" style={{ ...iconBtn, color: "#dc2626" }} title="Delete" aria-label={`Delete ${nameOf(item)}`} onClick={() => setConfirmDelete(item)}><Trash2 size={12} /></button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <TablePagination
                      total={data.total}
                      page={page}
                      limit={limit}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      onPageChange={setPage}
                      onLimitChange={(l) => { setLimit(l); setPage(1); }}
                    />
                  </>
                )}
              </div>
            </RCard>
          )}
        </div>
      </Container>

      <Modal isOpen={Boolean(confirmDelete)} toggle={() => !busy && setConfirmDelete(null)}>
        <ModalHeader toggle={() => !busy && setConfirmDelete(null)}>Delete this image?</ModalHeader>
        <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
          “<b style={{ color: C.text }}>{confirmDelete && nameOf(confirmDelete)}</b>” is deleted permanently.
          <div style={{ ...notice("error"), marginTop: 12, marginBottom: 0 }}>
            Its link stops working immediately. Any page, email or app using this link will show a broken image.
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
          <button type="button" onClick={() => setConfirmDelete(null)} disabled={busy} style={btnSecondary}>Back</button>
          <button type="button" onClick={runDelete} disabled={busy} style={btnDanger}>{busy ? "Deleting…" : "Yes, delete"}</button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default MediaLibrary;
